<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <progress-nav-component
      :steps="navSteps"
      :setStep="setStep"
    ></progress-nav-component>

    <CountryPickerComponent v-if="signupStep === 1" />

    <!-- Conditionally Rendered Components -->
    <grid-contract-upload-component
      v-if="shownStep.component === 'contract-upload'"
      :accountData="accountData"
      :hasGenerator="hasGenerator"
      :setAddAuthorizationAgreement="setAddAuthorizationAgreement"
      :setLoading="setLoading"
      @uploadComplete="handleUploadComplete"
      class="mb-4"
    ></grid-contract-upload-component>

    <generator-review-component
      v-if="
        shownStep.component === 'generator-review' &&
        hasGenerator &&
        accountData.companies[companyIndex].generators[generatorIndex]
      "
      @handleUpdateGenerator="signupStore.updateGenerator"
      :accountData="accountData"
      class="mb-4"
    ></generator-review-component>

    <ContactReviewConfigurationComponent
      v-if="shownStep.component === 'contact-review' && companyIndex !== -1"
      :accountData="accountData"
      :companyIndex="companyIndex"
      @handleUpdateCompany="signupStore.updateCompany"
      @updateAccountData="signupStore.updateAccount"
      class="mb-4"
    ></ContactReviewConfigurationComponent>

    <ContractReviewConfigurationComponent
      v-if="shownStep.component === 'contract-review' && companyIndex !== -1"
      :accountData="accountData"
      :setLoading="setLoading"
      @createContainerForStartSigningReady="storeCreateContainerForStartSigning"
      class="mb-4"
    ></ContractReviewConfigurationComponent>

    <!-- Display Error Message -->
    <div v-if="apiError" class="alert alert-danger">
      {{ apiErrorMessage }}
    </div>

    <!-- Buttons -->
    <div class="d-flex justify-content-between w-100">
      <button
        class="btn btn-secondary me-2"
        :disabled="signupStep === 1 || isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>
      <button
        v-if="shownStep.component !== 'contract-review'"
        class="btn btn-primary"
        :disabled="isLoading"
        @click="setStep(signupStep + 1)"
      >
        {{ $t("signup.navsteps.next") }}
      </button>
      <button
        v-if="shownStep.component === 'contract-review'"
        class="btn btn-primary"
        :disabled="isLoading"
        @click="createContainerForStartSigningFunction"
      >
        {{ $t("signup.navsteps.to_signature_page") }}
      </button>
    </div>
    <!-- Loading overlay, shown only when isLoading is true -->
    <LoadingOverlayComponent v-if="isLoading" />
    <!-- AccountDataDebugComponent for debugging -->
    <AccountDataDebugComponent
      :accountData="accountData"
      class="mb-4"
    ></AccountDataDebugComponent>
  </div>
  <!-- Greeting modal -->
  <ModalComponent
    :submitButtonText="$t('signup.modal.hello_button')"
    @setForceToggle="setToggleHelloModal"
  >
    <template #title>{{ $t("signup.modal.hello") }}</template>
    <template #body>
      <p class="text-body-tertiary lh-lg">
        {{ $t("signup.modal.ee.section1") }}
      </p>
      <p>
        {{ $t("signup.modal.ee.section2") }}
        <a
          class="link"
          role="button"
          href="https://www.soldera.org/#faq"
          target="blank"
        >
          <font-awesome-icon icon="right-to-bracket" />
          {{ $t("signup.modal.soldera_faq") }}
        </a>
      </p>
      <div class="accordion mt-3" id="accordionExample">
        <div class="accordion-item">
          <h2 class="accordion-header" id="headingTwo">
            <button
              class="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseTwo"
              aria-expanded="false"
              aria-controls="collapseTwo"
            >
              {{ $t("signup.modal.ee.section3") }}
            </button>
          </h2>
          <div
            id="collapseTwo"
            class="accordion-collapse collapse"
            aria-labelledby="headingTwo"
            data-bs-parent="#accordionExample"
          >
            <div class="accordion-body">
              <p>
                {{ $t("signup.modal.ee.section4") }}
              </p>
              <p>
                {{ $t("signup.modal.ee.section5") }}
              </p>
            </div>
          </div>
        </div>
        <div class="accordion-item">
          <h2 class="accordion-header" id="headingOne">
            <button
              class="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseOne"
              aria-expanded="false"
              aria-controls="collapseOne"
            >
              {{ $t("signup.modal.ee.section6") }}
            </button>
          </h2>
          <div
            id="collapseOne"
            class="accordion-collapse collapse"
            aria-labelledby="headingOne"
            data-bs-parent="#accordionExample"
          >
            <div class="accordion-body">
              <p>
                {{ $t("signup.modal.ee.section7") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import ContactReviewConfigurationComponent from "@/signup/ee/ContactReviewConfigurationComponent.vue";
import ContractReviewConfigurationComponent from "@/signup/ee/ContractReviewConfigurationComponent.vue";
import GeneratorReviewComponent from "@/signup/ee/GeneratorReviewComponent.vue";
import GridContractUploadComponent from "@/signup/ee/GridContractUploadComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import { useSignupStore } from "@/signup/signup";
import { storeToRefs } from "pinia";
import type { CompanySaveResult, GeneratorUploadResult, Step } from "@/types";
import useVuelidate from "@vuelidate/core";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const signupStep = ref(1);
const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);

const isLoading = ref(true);
const apiError = ref(false);
const apiErrorMessage = ref("");
const route = useRoute();
const router = useRouter();
const { globalErrorHandler } = useGlobalHandlers();
const signupStore = useSignupStore();
const {
  account: accountData,
  companyIndex,
  generatorIndex,
} = storeToRefs(signupStore);
const addAuthorizationAgreement = computed(() => {
  return route.query.addAuthorizationAgreement === "true";
});
const v$ = useVuelidate();
const { t } = useI18n();

const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

const toggleHelloModal = ref();
const setToggleHelloModal = (method: (value: boolean) => void) => {
  toggleHelloModal.value = method;
};
const toggleHelloIfNew = async () => {
  if (accountData.value.companies.length === 0) {
    toggleHelloModal.value(true);
  }
};
const setLoading = (value: boolean) => {
  isLoading.value = value;
};

const fetchUrl = () => {
  if (shownStep.value.component === "contact-review") {
    return "/api/ee/update-company";
  } else if (shownStep.value.component === "generator-review") {
    return "/api/ee/update-generator";
  } else {
    throw new Error("Invalid step for account " + accountData.value.id);
  }
};
const onAccountUpdated = async ({ company_id }: CompanySaveResult) => {
  isLoading.value = false;

  // This is here to only add the new company_id to the query parameters in case of authorization
  // agreement when company was created
  const newQuery = {
    ...route.query,
    company_id: company_id,
  };
  await router.push({ name: "ProducerSignUp", query: newQuery });
};

const setAddAuthorizationAgreement = (addAuthorizationAgreement: boolean) => {
  toggleHelloModal.value(false);
  const newQuery = {
    ...route.query,
    addAuthorizationAgreement: addAuthorizationAgreement.toString(),
    company_id: undefined,
    generator_id: undefined,
    step: "2",
  };
  signupStore.createCompanyIfMissing();
  router.push({ name: "ProducerSignUp", query: newQuery });
};

const checkValidity = () => {
  v$.value.$touch();
  return !v$.value.$error;
};

const setStep = async (step: number) => {
  isLoading.value = true;
  try {
    // If we are going back and we are in the Authrization flow we don't need to save the data or validate it
    // Otherwise we will try to validate.
    if (!(step < signupStep.value && addAuthorizationAgreement.value)) {
      if (!checkValidity()) {
        return;
      }

      // Since data is valid we can post it
      if (
        shownStep.value.component === "contact-review" ||
        shownStep.value.component === "generator-review"
      ) {
        await onAccountUpdated(await signupStore.saveDraft(fetchUrl()));
      }
    }

    signupStep.value = step;

    // Preserve the existing query parameters
    if (step === 1) {
      const newQuery = {
        ...route.query,
        addAuthorizationAgreement: undefined,
        step: step.toString(),
      };
      router.push({ name: "ProducerSignUp", query: newQuery });
    } else {
      const newQuery = {
        ...route.query,
        step: step.toString(),
      };
      router.push({ name: "ProducerSignUp", query: newQuery });
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

// Details which step is shown and whether or not the "next" button is enabled on that screen
const shownStep = computed(() => {
  if (addAuthorizationAgreement.value) {
    if (signupStep.value === 1) {
      return { component: "contract-upload" };
    } else if (signupStep.value === 2) {
      return {
        component: "contact-review",
      };
    } else if (signupStep.value === 3) {
      return {
        component: "contract-review",
      };
    }
  } else {
    if (signupStep.value === 1) {
      return { component: "contract-upload" };
    } else if (signupStep.value === 2) {
      return {
        component: "generator-review",
      };
    } else if (signupStep.value === 3) {
      return {
        component: "contact-review",
      };
    } else if (signupStep.value === 4) {
      return {
        component: "contract-review",
      };
    }
  }
  return { component: "contract-upload" };
});

const navCheck = (step: number) =>
  computed(() => {
    if (step === 1) {
      return true;
    } else {
      return signupStep.value - step >= -1;
    }
  });

const navSteps = computed(() => {
  return [
    {
      title: addAuthorizationAgreement.value
        ? t("signup.navsteps.ee.authorization.1")
        : t("signup.navsteps.ee.new_device.1"),
      isAvailable: navCheck(1).value,
    },
    {
      title: addAuthorizationAgreement.value
        ? t("signup.navsteps.ee.authorization.2")
        : t("signup.navsteps.ee.new_device.2"),
      isAvailable: navCheck(2).value,
    },
    {
      title: addAuthorizationAgreement.value
        ? t("signup.navsteps.ee.authorization.3")
        : t("signup.navsteps.ee.new_device.3"),
      isAvailable: navCheck(3).value,
    },
    !addAuthorizationAgreement.value
      ? {
          title: t("signup.navsteps.ee.new_device.4"),
          isAvailable: navCheck(4).value,
        }
      : null,
  ].filter(Boolean) as Step[]; // Remove null values
});

const hasGenerator = computed(() => {
  return generatorIndex.value !== -1;
});

const handleUploadComplete = async (responseData: GeneratorUploadResult) => {
  const generator_id = responseData.generator_id;
  const company_id = responseData.company_id;
  signupStore.takeAnswer(responseData.accountData);
  const newQuery = {
    ...route.query,
    step: "2",
    generator_id: generator_id,
    company_id: company_id,
  };
  router.push({ name: "ProducerSignUp", query: newQuery });
};

onMounted(async () => {
  if (
    addAuthorizationAgreement.value &&
    shownStep.value.component === "contact-review" &&
    companyIndex.value === -1
  ) {
    signupStore.createCompanyIfMissing();
  }
  isLoading.value = false;
  toggleHelloIfNew();
});

watch(
  () => route.query.step,
  (newStep) => {
    if (typeof newStep === "string") {
      const stepNum = parseInt(newStep, 10);
      if (!isNaN(stepNum) && stepNum >= 0 && stepNum <= 4) {
        signupStep.value = stepNum;
      }
    }
  },
  { immediate: true }
);
</script>
